/**
 * CustomMensagens.tsx — o MOTOR DE MENSAGENS na tela, plugável em qualquer item.
 *
 * A família (arquivo no plural, componente no singular):
 *
 *   CustomMensagensDoItem   as salas de um item + "Abrir sala". É o que se pluga:
 *                           `<CustomMensagensDoItem ancora={{ tipo: 'ordens_servico', id }} />`
 *   CustomMensagensSala     UMA sala: cabeçalho, mural em bolhas, compositor — o polling DENTRO
 *   CustomMensagensBadge    o número de não lidas de um item (para a aba)
 *   CustomMensagensRosto    o avatar pequeno (foto → iniciais)
 *
 * O MOLDE é o chat do tema (`theme/adminux/adminux-chat-message.html`),
 * transcrito para `estilos/_mensagens.scss` com nome nosso — decisão dele em
 * 02/09/2026: trazer o tema para perto e moldar aqui, sem editar o pacote.
 * O que ficou do tema: a minha mensagem à direita em bolha cheia, as dos
 * outros à esquerda em bolha clara com o nome dentro, hora com tique embaixo,
 * pílula de data, compositor em uma linha. O que é nosso: a citação da
 * resposta dentro da bolha, a linha do sistema, o cabeçalho de uma linha
 * (título · estado · rostos · menu) no lugar da foto de grupo, a rolagem fina,
 * e o microfone (ditado pelo navegador).
 *
 * Regras da casa que este arquivo cumpre:
 *   - tempo real é do componente: polling de 3 s só com a sala visível
 *     (`document.hidden` pausa) e por CURSOR (`?depois_de=`); a borda vira SSE
 *     trocando o `setInterval` por `EventSource`, e nada mais muda;
 *   - Enter NÃO envia — no celular Enter é quebra de linha; enviar é o botão;
 *   - foto comprimida no cliente ANTES de subir (`lib/imagem`), como a OS;
 *   - avatar com `.casaa-foto-no-avatar`, nunca `coverimg` (a lição de 30/08);
 *   - nenhuma cor cravada; nenhuma classe que "parece Bootstrap" (`min-w-0`,
 *     `me-n2` não existem — medido em 02/09).
 *
 * Previsto no modelo, não na v1: áudio e vídeo como anexo, e a rota SSE.
 */
import { useDitado } from '@/components/CustomBotaoDitado';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { toast } from 'sonner';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import { comprimirImagem } from '@/lib/imagem';
import { formatarDataExtenso, formatarHora, paraISO } from '@/lib/data';
import { mensagensApi } from '@/lib/mensagens';
import type { Ancora, Anexo, Mensagem, Pessoa, Sala } from '@/lib/mensagens';

const INTERVALO_POLLING_MS = 3000;
const INTERVALO_BADGE_MS = 60000;

function erroParaTexto(err: unknown, padrao: string) {
  return err instanceof ApiError ? err.message : padrao;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Peças pequenas
 * ═══════════════════════════════════════════════════════════════════════════ */
export function CustomMensagensRosto({ pessoa, tamanho = 32 }: { pessoa: Pessoa | null; tamanho?: 24 | 32 | 40 }) {
  const iniciais = (pessoa?.nome ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
  return (
    <span
      className="casaa-mensagens-rosto avatar rounded-circle bg-theme-1-subtle text-theme-1 d-inline-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden casaa-foto-no-avatar"
      /* O tamanho vai inline de propósito: `avatar-24/32` do tema não segurou
         a foto no celular (medido em 02/09 — o rosto ocupou a tela inteira). */
      style={{ width: tamanho, height: tamanho, minWidth: tamanho, fontSize: tamanho <= 24 ? '0.6rem' : '0.75rem' }}
      title={pessoa?.nome ?? ''}
    >
      {pessoa?.foto ? (
        <img src={pessoa.foto} alt="" className="rounded-circle object-fit-cover w-100 h-100" />
      ) : (
        <span className="fw-medium">{iniciais}</span>
      )}
    </span>
  );
}

/** O texto com as @menções destacadas.
 *
 * Exportado desde 04/09/2026: a prévia do chat no card da etapa mostrava
 * `@Adriana` como texto cinza qualquer — *"não tem a marcação da Adriana
 * direito"*. Quem escreve a menção e quem a lê de longe têm de ver a mesma
 * coisa, então o destaque mora num lugar só. */
export function TextoComMencoes({ texto, mencoes }: { texto: string; mencoes: { id: number; nome: string }[] }) {
  if (mencoes.length === 0) return <>{texto}</>;
  const nomes = mencoes.map((m) => m.nome).sort((a, b) => b.length - a.length);
  const regex = new RegExp(`@(${nomes.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  const partes = texto.split(regex);
  return (
    <>
      {partes.map((p, i) =>
        i % 2 === 1 ? (
          <span key={i} className="casaa-mensagens-mencao">@{p}</span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/** Junta sem repetir e em ordem de id — a ordem em que o servidor gravou. */
function juntar(atual: Mensagem[], novas: Mensagem[]): Mensagem[] {
  const porId = new Map(atual.map((m) => [m.id, m]));
  for (const n of novas) porId.set(n.id, n);
  return [...porId.values()].sort((a, b) => a.id - b.id);
}

function PilulaEstado({ estado }: { estado: Sala['estado'] }) {
  /* Selos da casa: o `text-bg-success` do Bootstrap dava 2,75 de contraste
     (branco sobre verde claro), medido em 04/09/2026. */
  const cls = estado === 'aberta' ? 'casaa-selo-sucesso' : estado === 'fechada' ? 'casaa-selo-neutro' : 'casaa-selo-neutro';
  const rotulo = estado === 'aberta' ? 'Aberta' : estado === 'fechada' ? 'Fechada' : 'Arquivada';
  return <span className={`badge ${cls}`}>{rotulo}</span>;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * A SALA
 * ═══════════════════════════════════════════════════════════════════════════ */
interface SalaProps {
  salaId: number;
  /**
   * A ETAPA de onde se está falando (095). Duas coisas, e as duas importam:
   * o que se escreve daqui nasce marcado com ela, e a lista abre FILTRADA nela
   * — com "ver a conversa toda" ao lado.
   *
   * Dele, 05/09, duvidando do desenho que ele mesmo pediu: *"de repente você
   * vai começar a se perder — essa mensagem aqui a gente mandou em qual
   * etapa?"*. A resposta não foi uma sala por etapa: foi a mensagem dizer de
   * onde veio.
   */
  etapaId?: number;
  /** Como a etiqueta da etapa aparece na conversa: `{12: "2.0"}`. */
  numerosDasEtapas?: Record<number, string>;
  /** Chamado quando a sala muda de estado, ou quando o cursor anda (para a lista atualizar o badge). */
  aoMudar?: (sala: Sala) => void;
  /** Esconde o cabeçalho (quando quem pluga já mostra título e estado). */
  semCabecalho?: boolean;
  /**
   * RECOLHE as linhas de sistema ("Fulano abriu a sala"). Para o painel lateral,
   * onde o espaço é curto e quem entra quer ver CONVERSA.
   *
   * É a mesma regra que o card da etapa já aplica desde 04/09 — *"não tem nada
   * que botar que Vittorio abriu a sala; o registro é importante, mas fica lá
   * dentro"* — e a mesma da memória `registro-nao-e-log`: a linha aparece se tem
   * texto de gente. Elas não somem do banco nem da aba: só saem do resumo.
   */
  soFalas?: boolean;
}

export function CustomMensagensSala({
  salaId, etapaId, numerosDasEtapas, aoMudar, semCabecalho, soFalas,
}: SalaProps) {
  /* Dentro de uma etapa a conversa abre filtrada nela — é o que a pessoa foi
     ver. O interruptor mostra o caso inteiro sem sair da página. */
  const [soDestaEtapa, setSoDestaEtapa] = useState(true);
  const { usuario, pode } = useAuth();
  const [sala, setSala] = useState<Sala | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [texto, setTexto] = useState('');
  const [respondendo, setRespondendo] = useState<Mensagem | null>(null);
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [subindo, setSubindo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [mencionaveis, setMencionaveis] = useState<Pessoa[]>([]);
  const [mencionados, setMencionados] = useState<Pessoa[]>([]);
  const [sugestoes, setSugestoes] = useState<Pessoa[]>([]);
  /* Qual sugestão está marcada — o teclado precisa de um "aqui". Sem isto, a
     única forma de marcar alguém era digitar o nome inteiro ou tirar a mão do
     teclado para clicar, que foi a queixa dele em 04/09/2026. */
  const [sugestaoAtiva, setSugestaoAtiva] = useState(0);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [gerindo, setGerindo] = useState(false);
  const [novaPessoaId, setNovaPessoaId] = useState('');
  /* ⛔ PEDIR ID NUMÉRICO A UM HUMANO NÃO É OPÇÃO DE TELA. Era por isso que ele
     não achava onde adicionar alguém — dele, 04/09: *"quem que você adiciona,
     adiciona onde? cadê?"*. O campo existia, e pedia o número do usuário.
     Agora é a lista de gente, sem quem já está na sala. */
  const [pessoas, setPessoas] = useState<{ id: number; nome: string }[]>([]);
  const [estenderTipo, setEstenderTipo] = useState('ordens_servico');
  const [estenderId, setEstenderId] = useState('');

  const fimRef = useRef<HTMLDivElement>(null);
  const campoRef = useRef<HTMLTextAreaElement>(null);
  const ultimoId = useMemo(() => (mensagens.length ? mensagens[mensagens.length - 1].id : 0), [mensagens]);
  const ultimoIdRef = useRef(0);
  ultimoIdRef.current = ultimoId;

  const podeApagarDosOutros = pode('mensagens.sala.apagar');
  // O ditado é a peça `CustomBotaoDitado` (o gancho): o botão daqui tem o
  // desenho da barra da conversa, por isso usa o gancho e não o botão pronto.
  const ditado = useDitado(texto, setTexto);

  // Carga inicial: a sala, o mural inteiro, os mencionáveis.
  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);
    setMensagens([]);
    (async () => {
      try {
        const [s, ms] = await Promise.all([mensagensApi.sala(salaId), mensagensApi.mensagens(salaId, 0)]);
        if (!vivo) return;
        setSala(s);
        setMensagens(ms);
        aoMudar?.(s);
        mensagensApi.mencionaveis(salaId).then((p) => vivo && setMencionaveis(p)).catch(() => undefined);
      } catch (err) {
        if (vivo) setErro(erroParaTexto(err, 'Não deu para abrir a sala.'));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaId]);

  // O POLLING — por cursor, só com a janela visível. A resposta quase sempre
  // é `[]`, e custa um índice. Trocar por SSE = trocar este efeito.
  // A cada 20 s também recarrega a sala (os tiques de "lido por todos").
  useEffect(() => {
    if (carregando || erro) return;
    let tiques = 0;
    const tique = async () => {
      if (document.hidden) return;
      try {
        tiques += 1;
        let chegou = false;
        if (tiques % 4 === 0) {
          /* A MENSAGEM APAGADA SAI DA TELA DOS OUTROS. O cursor só traz o que é
             novo e nunca sabia de uma remoção: a Suany, com a sala aberta,
             continuava vendo o que o Diogo apagou (ensaio de 29/10/2026). A cada
             quatro tiques (~12 s) a lista vem inteira e substitui a da tela. */
          const todas = await mensagensApi.mensagens(salaId, 0);
          chegou = todas.length > 0 && todas[todas.length - 1].id > ultimoIdRef.current;
          setMensagens((atual) => {
            const ids = new Set(todas.map((m) => m.id));
            const sumiram = atual.some((m) => !ids.has(m.id));
            return sumiram ? todas : juntar(atual, todas);
          });
        } else {
          const novas = await mensagensApi.mensagens(salaId, ultimoIdRef.current);
          if (novas.length > 0) setMensagens((atual) => juntar(atual, novas));
          chegou = novas.length > 0;
        }
        if (chegou || tiques % 7 === 0) {
          const s = await mensagensApi.sala(salaId);
          setSala((atual) => (atual ? { ...atual, ...s, meu_cursor: Math.max(atual.meu_cursor, s.meu_cursor) } : s));
        }
      } catch {
        /* silencioso: o polling não pode gerar ruído */
      }
    };
    const t = setInterval(tique, INTERVALO_POLLING_MS);
    return () => clearInterval(t);
  }, [salaId, carregando, erro]);

  // Ao ver mensagens novas, o cursor anda (e o badge da aba zera).
  useEffect(() => {
    if (!sala || ultimoId === 0 || ultimoId <= sala.meu_cursor) return;
    if (document.hidden) return;
    mensagensApi
      .marcarLido(salaId, ultimoId)
      .then((r) => {
        setSala((s) => (s ? { ...s, meu_cursor: r.ultimo_lido, nao_lidas: 0 } : s));
        if (sala) aoMudar?.({ ...sala, meu_cursor: r.ultimo_lido, nao_lidas: 0 });
      })
      .catch(() => undefined);
    fimRef.current?.scrollIntoView({ block: 'end' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ultimoId, sala?.id]);

  // ─── O campo cresce com o texto ───────────────────────────────────────────
  function ajustarAltura() {
    const el = campoRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 144)}px`;
  }
  /**
   * DEVOLVE O CAMPO À ALTURA BASE, sem calcular nada.
   *
   * ⛔ Ele relatou em 11/09/2026, no TABLET: depois de mandar uma mensagem
   * ditada, *"a caixa onde eu digitaria a mensagem ficou deformada, mais alta
   * que os botões laterais"*.
   *
   * A altura vinha só do efeito abaixo, que depende de `texto` MUDAR — e no
   * ditado o texto entra por fora (`SpeechRecognition`), de forma assíncrona.
   * A ordem entre o que a voz escreve e o `setTexto('')` do envio não é
   * garantida, então o campo podia ficar com a altura em pixels de um texto que
   * já tinha ido embora.
   *
   * Apagar a altura inline (`''`, e não `'auto'`) devolve o controle ao CSS e ao
   * `rows={1}`: não há o que recalcular quando não há texto.
   */
  function zerarAltura() {
    const el = campoRef.current;
    if (el) el.style.height = '';
  }

  useEffect(ajustarAltura, [texto]);

  // ─── O `@` ────────────────────────────────────────────────────────────────
  function aoDigitar(v: string) {
    setTexto(v);
    const m = v.match(/@([\p{L}0-9]*)$/u);
    if (!m) {
      setSugestoes([]);
      return;
    }
    const termo = m[1].toLowerCase();
    setSugestoes(mencionaveis.filter((p) => p.nome.toLowerCase().includes(termo)).slice(0, 6));
    setSugestaoAtiva(0);
  }

  /* O TECLADO NA LISTA DE MENÇÃO — ↑ ↓ andam, Enter e Tab marcam, Esc fecha.
     Enter só envia a mensagem quando NÃO há lista aberta; com a lista aberta
     ele marca a pessoa, que é o que todo chat faz. */
  function aoTeclar(ev: React.KeyboardEvent<HTMLTextAreaElement>) {
    /* ⛔ ENTER ENVIA. SHIFT+ENTER QUEBRA A LINHA.
       Dele, 05/09/2026: *"o Enter depois de você mandar a mensagem não
       funciona. Eu prefiro shift+enter para quebrar linha — Enter envia"*. E
       estava mesmo morto: a função saía fora quando não havia sugestão aberta,
       então o Enter só servia para escolher uma menção.

       `isComposing` é o acento morto e o IME: no meio de compor um caractere o
       Enter é do teclado, não nosso — sem isto, quem digita "ç" ou "ã" em
       alguns layouts manda a mensagem pela metade. */
    if (sugestoes.length === 0) {
      if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
        ev.preventDefault();
        enviar();
      }
      return;
    }
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setSugestaoAtiva((i) => (i + 1) % sugestoes.length);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setSugestaoAtiva((i) => (i - 1 + sugestoes.length) % sugestoes.length);
    } else if (ev.key === 'Enter' || ev.key === 'Tab') {
      ev.preventDefault();
      escolherMencao(sugestoes[sugestaoAtiva] ?? sugestoes[0]);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      setSugestoes([]);
    }
  }
  function escolherMencao(p: Pessoa) {
    setTexto((v) => v.replace(/@([\p{L}0-9]*)$/u, `@${p.nome} `));
    setMencionados((arr) => (arr.some((x) => x.id === p.id) ? arr : [...arr, p]));
    setSugestoes([]);
    setSugestaoAtiva(0);
    campoRef.current?.focus();
  }

  // ─── Foto ─────────────────────────────────────────────────────────────────
  async function subirFotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    setSubindo(true);
    try {
      for (const f of Array.from(files)) {
        const comprimida = await comprimirImagem(f);
        const anexo = await mensagensApi.subirAnexo(comprimida);
        setAnexos((arr) => [...arr, anexo]);
      }
    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para subir a foto.'));
    } finally {
      setSubindo(false);
    }
  }

  // ─── Enviar ───────────────────────────────────────────────────────────────
  async function enviar() {
    /* `subindo` também: o botão já esperava a foto, o Enter não — a mensagem
       saía sem ela e a foto ficava para a próxima (ensaio, rodada 2). */
    if (!sala || enviando || subindo) return;
    const t = texto.trim();
    if (!t && anexos.length === 0) return;
    setEnviando(true);
    /* O cursor de ANTES de mandar. Sem ele, a mensagem que o outro mandou no
       mesmo instante (id menor que a minha) nunca chegava: o polling passava a
       pedir só o que veio depois da MINHA, e ainda a marcava como lida
       (ensaio de 29/10/2026 — aconteceu 6 vezes sem querer). */
    const antes = ultimoIdRef.current;
    try {
      const ativos = mencionados.filter((p) => t.includes(`@${p.nome}`)).map((p) => p.id);
      const nova = await mensagensApi.enviar(salaId, {
        texto: t || undefined,
        responde_id: respondendo?.id,
        anexos: anexos.map((a) => a.id),
        mencoes: ativos,
        etapa_id: etapaId,
      });
      const doMeio = await mensagensApi.mensagens(salaId, antes).catch(() => [] as Mensagem[]);
      setMensagens((arr) => juntar(arr, [...doMeio, nova]));
      setTexto('');
      zerarAltura();
      setAnexos([]);
      setMencionados([]);
      setRespondendo(null);
      setSugestoes([]);
    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para enviar.'));
    } finally {
      setEnviando(false);
    }
  }

  async function remover(m: Mensagem) {
    try {
      await mensagensApi.removerMensagem(salaId, m.id);
      setMensagens((arr) => arr.filter((x) => x.id !== m.id));
    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para remover.'));
    }
  }

  function acoesDa(m: Mensagem, minha: boolean) {
    if (!sala) return null;
    return (
      <span className="casaa-mensagens-acoes">
        {sala.posso_falar && (
          <button type="button" className="btn btn-link btn-sm text-secondary" title="Responder" aria-label="Responder" onClick={() => setRespondendo(m)}>
            <i className="bi bi-reply"></i>
          </button>
        )}
        {(minha || podeApagarDosOutros) && (
          <button type="button" className="btn btn-link btn-sm text-secondary" title="Remover" aria-label="Remover mensagem" onClick={() => remover(m)}>
            <i className="bi bi-trash"></i>
          </button>
        )}
      </span>
    );
  }

  async function acaoSala(acao: 'fechar' | 'reabrir' | 'arquivar') {
    try {
      const s = await mensagensApi[acao](salaId);
      setSala(s);
      aoMudar?.(s);
      const novas = await mensagensApi.mensagens(salaId, ultimoIdRef.current);
      if (novas.length) setMensagens((arr) => [...arr, ...novas]);
    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para mudar a sala.'));
    }
  }

  /* Só quando o painel abre: quem nunca gerencia a sala não paga a consulta. */
  useEffect(() => {
    if (!gerindo || pessoas.length) return;
    api.get<{ id: number; nome: string }[]>('/usuarios/opcoes')
      .then(setPessoas)
      .catch(() => undefined);
  }, [gerindo, pessoas.length]);

  async function adicionarPessoa() {
    const id = Number(novaPessoaId);
    if (!id) return;
    try {
      const s = await mensagensApi.adicionar(salaId, id, 'fala');
      setSala(s);
      setNovaPessoaId('');
      // "Fulano adicionou Beltrano" aparece na hora, não no próximo tique.
      mensagensApi.mensagens(salaId, ultimoIdRef.current)
        .then((novas) => novas.length && setMensagens((atual) => juntar(atual, novas)))
        .catch(() => undefined);
      mensagensApi.mencionaveis(salaId).then(setMencionaveis).catch(() => undefined);
    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para adicionar.'));
    }
  }

  async function estender() {
    const id = Number(estenderId);
    if (!id) return;
    try {
      const s = await mensagensApi.estender(salaId, { tipo: estenderTipo, id });
      setSala(s);
      setEstenderId('');
      toast.success('Sala estendida.');
    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para estender.'));
    }
  }

  // ─── O mural: dia, sequência da mesma pessoa, tiques ─────────────────────
  /* ⛔ O FILTRO VEM ANTES DO AGRUPAMENTO POR DIA. Filtrar depois deixaria
     "quinta-feira" sozinha no mural, sem nenhuma mensagem embaixo — a tarja do
     dia é sobre o que está à vista, não sobre o que existe. */
  const visiveis = useMemo(
    () => {
      let lista = etapaId && soDestaEtapa
        ? mensagens.filter((m) => m.etapa_id === etapaId)
        : mensagens;
      // `soFalas` no painel lateral: "Fulano abriu a sala" ocupa o lugar de uma
      // fala de verdade numa caixa de 18rem. Some do RESUMO, não do registro —
      // a aba continua mostrando tudo.
      if (soFalas) lista = lista.filter((m) => m.origem === 'pessoa');
      return lista;
    },
    [mensagens, etapaId, soDestaEtapa, soFalas],
  );

  const linhas = useMemo(() => {
    const saida: { m: Mensagem; novoDia: string | null; seguida: boolean }[] = [];
    let diaAnterior = '';
    for (let i = 0; i < visiveis.length; i++) {
      const m = visiveis[i];
      const dia = m.criacao ? paraISO(m.criacao) : '';
      const novoDia = dia && dia !== diaAnterior ? dia : null;
      diaAnterior = dia || diaAnterior;
      const prox = visiveis[i + 1];
      const seguida =
        !!prox &&
        m.origem === 'pessoa' &&
        prox.origem === 'pessoa' &&
        prox.autor?.id === m.autor?.id &&
        (prox.criacao ? paraISO(prox.criacao) : '') === dia;
      saida.push({ m, novoDia, seguida });
    }
    return saida;
  }, [mensagens]);

  // ─── Render ───────────────────────────────────────────────────────────────
  if (carregando) {
    return (
      <div className="d-flex align-items-center gap-2 text-secondary small py-3">
        <span className="spinner-border spinner-border-sm" /> Abrindo a sala…
      </div>
    );
  }
  if (erro || !sala) {
    return <div className="alert alert-light small mb-0">{erro ?? 'Sala indisponível.'}</div>;
  }

  return (
    <div className="d-flex flex-column">
      {/* ─── O CABEÇALHO: título · estado · rostos · menu ─── */}
      {!semCabecalho && (
        <div className="casaa-mensagens-cabecalho">
          <h6 className="mb-0 text-truncate" title={sala.titulo}>
            {sala.titulo}
          </h6>
          <PilulaEstado estado={sala.estado} />
          {sala.visibilidade === 'restrita' && (
            <i className="bi bi-lock text-secondary" title="Só quem está na sala vê"></i>
          )}
          <span className="casaa-mensagens-rostos ms-auto" title={sala.participantes.map((p) => p.nome).join(', ')}>
            {sala.participantes.slice(0, 5).map((p) => (
              <CustomMensagensRosto key={p.id} pessoa={p} tamanho={24} />
            ))}
            {sala.participantes.length > 5 && (
              <small className="text-secondary ms-2">+{sala.participantes.length - 5}</small>
            )}
          </span>
          <Dropdown align="end">
            <Dropdown.Toggle as="button" type="button" className="btn btn-sm btn-link btn-square text-secondary p-0 caret-none" aria-label="Menu da sala">
              <i className="bi bi-three-dots-vertical"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header className="small">
                Aberta por {sala.aberta_por.nome}
                {sala.ancoras.length > 0 && (
                  <>
                    <br />
                    {sala.ancoras.map((a, i) => (
                      <span key={`${a.tipo}:${a.id}`}>
                        {i > 0 && ' · '}
                        <a href={a.link}>{a.titulo ?? `${a.tipo} ${a.id}`}</a>
                      </span>
                    ))}
                  </>
                )}
              </Dropdown.Header>
              {sala.posso_gerenciar && (
                <>
                  <Dropdown.Divider />
                  {sala.estado === 'aberta' ? (
                    <Dropdown.Item onClick={() => acaoSala('fechar')}>
                      <i className="bi bi-lock me-2"></i>Fechar sala
                    </Dropdown.Item>
                  ) : (
                    <Dropdown.Item onClick={() => acaoSala('reabrir')}>
                      <i className="bi bi-unlock me-2"></i>Reabrir
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item onClick={() => setGerindo((g) => !g)}>
                    <i className="bi bi-people me-2"></i>Pessoas e itens da sala
                  </Dropdown.Item>
                  {sala.estado !== 'arquivada' && (
                    <Dropdown.Item onClick={() => acaoSala('arquivar')}>
                      <i className="bi bi-archive me-2"></i>Arquivar
                    </Dropdown.Item>
                  )}
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}

      {/* ⛔ QUEM VÊ SE LÊ, NÃO SE ESCOLHE. Dele, 11/09: *"quem está vendo? eu não
          estou entendendo, porque eu não poderia escolher, eu não estou escolhendo
          nada"*. Antes disto a resposta existia em dois lugares ruins: um `title`
          nos rostos (que no celular ninguém alcança) e um comentário no código.
          A visibilidade da sala é a regra; os rostos são quem ela alcança hoje. */}
      {!semCabecalho && (
        <p className="casaa-mensagens-quem-ve small text-secondary mb-0">
          {/* ⛔ QUEM VÊ e QUEM ESTÁ NA SALA são coisas diferentes numa sala
              ABERTA, e a primeira versão desta linha misturava as duas: dizia
              "vê quem tem acesso a este item: Vittorio, Suany", o que faz os
              dois nomes parecerem a lista completa de quem enxerga. Não são —
              são os participantes, que é outra coisa: quem recebe aviso e quem
              pode ser mencionado. Numa sala FECHADA as duas listas coincidem, e
              é só lá que o "só" cabe. */}
          {sala.visibilidade === 'restrita' ? (
            <>
              <i className="bi bi-lock-fill me-1"></i>
              <strong>Chat fechado</strong> — vê só quem está nele
              {sala.participantes.length > 0 && (
                <>: {sala.participantes.map((p) => p.nome).join(', ')}</>
              )}
            </>
          ) : (
            <>
              <i className="bi bi-eye me-1"></i>
              Vê quem tem acesso a este item.
              {sala.participantes.length > 0 && (
                <> Na sala: {sala.participantes.map((p) => p.nome).join(', ')}</>
              )}
            </>
          )}
        </p>
      )}

      {gerindo && sala.posso_gerenciar && (
        <div className="card adminuiux-card mb-3">
          <div className="card-body py-2 d-flex flex-column gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="small text-secondary">Na sala:</span>
              {sala.participantes.map((p) => (
                <span key={p.id} className="badge text-bg-light border d-inline-flex align-items-center gap-1">
                  {p.nome}
                  <small className="text-secondary">· {p.papel}</small>
                  {/* "Tirar" só na sala RESTRITA. Na aberta quem vê é quem
                      vê a OS: tirar não tirava nada, e a pessoa voltava ao
                      escrever (ensaio de 29/10/2026). */}
                  {p.id !== sala.aberta_por.id && sala.visibilidade === 'restrita' && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-secondary"
                      aria-label={`Tirar ${p.nome}`}
                      onClick={() =>
                        mensagensApi
                          .remover(salaId, p.id)
                          .then(setSala)
                          .catch((err) => toast.error(erroParaTexto(err, 'Não deu para tirar.')))
                      }
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </span>
              ))}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <div className="input-group input-group-sm" style={{ maxWidth: '22rem' }}>
                <span className="input-group-text">Quem mais participa</span>
                <select
                  className="form-select"
                  value={novaPessoaId}
                  onChange={(e) => setNovaPessoaId(e.target.value)}
                >
                  <option value="">— escolha a pessoa —</option>
                  {pessoas
                    .filter((p) => !(sala.participantes ?? []).some((q) => q.id === p.id))
                    .map((p) => (
                      <option key={p.id} value={String(p.id)}>{p.nome}</option>
                    ))}
                </select>
                <button type="button" className="btn btn-outline-theme"
                        disabled={!novaPessoaId} onClick={adicionarPessoa}>
                  Adicionar
                </button>
              </div>
              {/* A conversa da OS é só da OS (29/10/2026): não se estende. */}
              {!sala.ancoras.some((a) => a.tipo === 'ordens_servico') && (
              <div className="input-group input-group-sm" style={{ maxWidth: '22rem' }}>
                <span className="input-group-text">Estender a</span>
                <select className="form-select" value={estenderTipo} onChange={(e) => setEstenderTipo(e.target.value)}>
                  <option value="ordens_servico">OS</option>
                  <option value="autorizacoes">Autorização</option>
                </select>
                <input
                  className="form-control"
                  inputMode="numeric"
                  placeholder="id"
                  value={estenderId}
                  onChange={(e) => setEstenderId(e.target.value.replace(/\D/g, ''))}
                />
                <button type="button" className="btn btn-outline-theme" onClick={estender}>
                  Estender
                </button>
              </div>
              )}
              <button type="button" className="btn btn-sm btn-link ms-auto" onClick={() => setGerindo(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── O MURAL ─── */}
      <div className="casaa-mensagens-mural d-flex flex-column">
        {/* A CONVERSA É DO CASO, e esta página é de uma etapa: a barra diz o
            que se está vendo e deixa abrir o resto sem sair daqui. */}
        {etapaId && (
          <div className="d-flex align-items-center gap-2 mb-2 small">
            <span className="text-secondary">
              {soDestaEtapa
                ? 'Mostrando o que foi falado nesta etapa.'
                : 'Mostrando a conversa inteira da autorização.'}
            </span>
            <button type="button" className="btn btn-link btn-sm p-0"
                    onClick={() => setSoDestaEtapa((v) => !v)}>
              {soDestaEtapa ? 'Ver a conversa toda' : 'Só desta etapa'}
            </button>
          </div>
        )}
        {visiveis.length === 0 && (
          <p className="small text-secondary mb-3">
            {etapaId && soDestaEtapa && mensagens.length > 0
              ? 'Nada foi falado nesta etapa ainda — a conversa do caso tem mensagens.'
              : 'Nenhuma mensagem ainda. O que se combinar aqui fica registrado.'}
          </p>
        )}
        {linhas.map(({ m, novoDia, seguida }) => {
          const minha = m.origem === 'pessoa' && usuario?.id === m.autor?.id;
          const lidaPorTodos = minha && sala.lido_por_todos_ate != null && m.id <= sala.lido_por_todos_ate;
          return (
            <div key={m.id}>
              {novoDia && (
                <div className="text-center">
                  <span className="casaa-mensagens-pilula">{formatarDataExtenso(m.criacao ?? '')}</span>
                </div>
              )}
              {m.origem === 'sistema' ? (
                <div className="text-center">
                  <span className="casaa-mensagens-pilula">
                    {m.texto} · {m.criacao ? formatarHora(m.criacao) : ''}
                  </span>
                </div>
              ) : (
                <div className={`casaa-mensagens-linha${minha ? ' casaa-mensagens-linha--minha' : ''}${seguida ? ' casaa-mensagens-linha--seguida' : ''}`}>
                  {seguida ? (
                    <span className="casaa-mensagens-lugar-do-rosto" />
                  ) : (
                    <CustomMensagensRosto pessoa={m.autor} tamanho={32} />
                  )}
                  <div className="casaa-mensagens-coluna">
                  <div className="casaa-mensagens-bolha" tabIndex={0}>
                    {!minha && !seguidaDaAnterior(linhas, m) && (
                      <span className="casaa-mensagens-autor">{m.autor?.nome ?? 'Alguém'}</span>
                    )}
                    {/* DE QUAL ETAPA a pessoa falou. Só aparece quando a
                        etiqueta acrescenta: na conversa do caso, e na etapa
                        quando se está vendo tudo. Repeti-la na visão filtrada
                        seria dizer o que a página já diz. */}
                    {m.etapa_id && (!etapaId || !soDestaEtapa) && (
                      <span className="badge casaa-selo-neutro align-self-start mb-1">
                        {/* ⛔ "De uma etapa" NÃO DIZIA NADA, e ele bateu nisso
                            em 11/09: *"o que é 'De uma etapa'?"*. A etiqueta
                            existe para responder QUAL — o texto genérico era
                            ruído com cara de informação. Quando ela cai aqui, é
                            porque quem plugou a sala esqueceu de passar
                            `numerosDasEtapas`; "Etapa" ao menos não promete
                            responder o que não sabe. */}
                        {numerosDasEtapas?.[m.etapa_id]
                          ? `Etapa ${numerosDasEtapas[m.etapa_id]}`
                          : 'Etapa'}
                      </span>
                    )}
                    {m.responde && (
                      <span className="casaa-mensagens-citacao">
                        <strong>{m.responde.autor_nome ?? 'Alguém'}</strong>: {m.responde.trecho}
                      </span>
                    )}
                    {m.anexos.map((a) =>
                      a.tipo === 'foto' ? (
                        <img
                          key={a.id}
                          src={a.url}
                          alt={a.nome ?? 'foto'}
                          className="casaa-mensagens-foto"
                          onClick={() => setLightbox(a.url)}
                        />
                      ) : (
                        <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="d-block small">
                          <i className="bi bi-paperclip me-1"></i>
                          {a.nome ?? a.tipo}
                        </a>
                      ),
                    )}
                    {m.texto && (
                      <p className="casaa-mensagens-texto">
                        <TextoComMencoes texto={m.texto} mencoes={m.mencoes} />
                      </p>
                    )}
                  </div>
                  {!seguida && (
                    <span className="casaa-mensagens-hora">
                      {minha && (
                        <i
                          className={`bi ${lidaPorTodos ? 'bi-check-all text-primary' : 'bi-check'}`}
                          title={lidaPorTodos ? 'Lida por todos' : 'Enviada'}
                        ></i>
                      )}
                      {m.criacao ? formatarHora(m.criacao) : ''}
                      {acoesDa(m, minha)}
                    </span>
                  )}
                  {/* A mensagem do MEIO de uma sequência também se responde e
                      se apaga: antes só a última tinha os botões, e para apagar
                      a primeira era preciso apagar a segunda (ensaio de
                      29/10/2026). Sem hora — ela é da última —, e só aparece
                      quando se passa o mouse ou se toca na bolha. */}
                  {seguida && (
                    <span className="casaa-mensagens-hora casaa-mensagens-hora--seguida">
                      {acoesDa(m, minha)}
                    </span>
                  )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <div ref={fimRef} />
      </div>

      {/* ─── O COMPOSITOR ─── */}
      {sala.estado !== 'aberta' ? (
        <div className="small text-secondary border-top pt-2 mt-2">
          <i className="bi bi-lock me-1"></i>
          Sala {sala.estado === 'fechada' ? 'fechada' : 'arquivada'}.
          {sala.posso_gerenciar && sala.estado === 'fechada' && (
            <button type="button" className="btn btn-sm btn-link p-0 ms-2" onClick={() => acaoSala('reabrir')}>
              Reabrir
            </button>
          )}
        </div>
      ) : !sala.posso_falar ? (
        <div className="small text-secondary border-top pt-2 mt-2">
          <i className="bi bi-eye me-1"></i>Você só lê neste chat.
        </div>
      ) : (
        <div className="casaa-mensagens-compositor border-top pt-2 mt-2 d-flex flex-column gap-2">
          {respondendo && (
            <div className="d-flex align-items-start gap-2 small">
              <span className="casaa-mensagens-citacao flex-grow-1 mb-0">
                <strong>Respondendo a {respondendo.autor?.nome ?? 'alguém'}</strong>: {(respondendo.texto ?? '📷 foto').slice(0, 120)}
              </span>
              <button type="button" className="btn btn-link p-0 text-secondary" aria-label="Cancelar resposta" onClick={() => setRespondendo(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}
          {anexos.length > 0 && (
            <div className="d-flex flex-wrap gap-2">
              {anexos.map((a, i) => (
                <div key={a.id} className="position-relative">
                  <img src={a.url} alt={a.nome ?? 'foto'} className="rounded border object-fit-cover" style={{ width: '3rem', height: '3rem' }} />
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 position-absolute top-0 end-0 translate-middle-y text-danger bg-body rounded-circle"
                    aria-label="Tirar foto"
                    onClick={() => setAnexos((arr) => arr.filter((_, k) => k !== i))}
                  >
                    <i className="bi bi-x-circle-fill"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          {sugestoes.length > 0 && (
            <div className="casaa-mensagens-sugestoes bg-body border rounded shadow-sm py-1 mb-1">
              {sugestoes.map((p, i) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => escolherMencao(p)}
                  onMouseEnter={() => setSugestaoAtiva(i)}
                  className={`btn btn-link text-decoration-none text-start w-100 px-3 py-1 d-flex align-items-center gap-2${
                    i === sugestaoAtiva ? ' casaa-mensagens-sugestao--ativa' : ''
                  }`}
                >
                  <CustomMensagensRosto pessoa={p} tamanho={24} />@{p.nome}
                </button>
              ))}
            </div>
          )}
          <div className="input-group align-items-end">
            <label className="btn btn-square btn-link border mb-0 d-flex align-items-center justify-content-center" title="Anexar foto">
              {subindo ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-camera"></i>}
              <input
                type="file"
                accept="image/*"
                multiple
                className="d-none"
                disabled={subindo}
                onChange={(e) => {
                  subirFotos(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            <textarea
              ref={campoRef}
              /* SEM `bg-none`: com fundo transparente o campo desaparece dentro
                 do card branco. No chat do tema ele é sólido (medido 04/09). */
              className="form-control"
              rows={1}
              placeholder="Escreva… use @ para mencionar"
              aria-label="Mensagem"
              value={texto}
              onChange={(e) => aoDigitar(e.target.value)}
              onKeyDown={aoTeclar}
            />
            {/* ⛔ MENCIONAR SEM CAÇAR O `@` NO TECLADO. Dele, 11/09, testando no
                TABLET: *"você tem alguma ideia para que as pessoas sejam
                marcadas nessa caixa de alguma forma que não envolva a digitação
                do arroba? A digitação do arroba é pouco prática"*.

                Não é atalho novo nem lista nova: o botão escreve o `@` no campo
                e dispara a MESMA busca que digitá-lo dispara — uma porta a mais
                para o mecanismo que já existe, não um segundo mecanismo. */}
            <button
              type="button"
              className="btn btn-square btn-link border"
              title="Mencionar alguém"
              aria-label="Mencionar alguém"
              onClick={() => {
                const base = texto && !texto.endsWith(' ') ? `${texto} ` : texto;
                aoDigitar(`${base}@`);
                campoRef.current?.focus();
              }}
            >
              <i className="bi bi-at"></i>
            </button>
            <button
              type="button"
              className={`btn btn-square btn-link border${ditado.ouvindo ? ' casaa-mensagens-mic--ouvindo' : ''}`}
              title={ditado.disponivel ? (ditado.ouvindo ? 'Parar o ditado' : 'Ditar') : 'Ditado indisponível neste navegador'}
              aria-label="Ditar"
              disabled={!ditado.disponivel}
              onClick={ditado.alternar}
            >
              <i className={`bi ${ditado.ouvindo ? 'bi-mic-fill' : 'bi-mic'}`}></i>
            </button>
            {/* O BOTÃO NÃO SE APAGA COM O CAMPO VAZIO. `disabled` o deixava a
                50% de opacidade — um avião cinza que parece quebrado, e foi a
                primeira coisa que ele não achou na tela. O tema mantém o botão
                de enviar sempre vivo; quem não deixa mandar mensagem vazia é o
                `enviar()`, que já sai fora sem texto nem anexo. */}
            <button
              type="button"
              className="btn btn-square btn-theme"
              title="Enviar"
              aria-label="Enviar"
              onClick={enviar}
              disabled={enviando || subindo}
            >
              {enviando ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-send"></i>}
            </button>
          </div>
        </div>
      )}

      <Lightbox open={lightbox !== null} close={() => setLightbox(null)} slides={lightbox ? [{ src: lightbox }] : []} />
    </div>
  );
}

/** A mensagem anterior é da mesma pessoa, no mesmo dia? (então o nome não se repete) */
function seguidaDaAnterior(linhas: { m: Mensagem; seguida: boolean }[], m: Mensagem): boolean {
  const i = linhas.findIndex((l) => l.m.id === m.id);
  return i > 0 && linhas[i - 1].seguida;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * AS SALAS DE UM ITEM — o que se pluga
 * ═══════════════════════════════════════════════════════════════════════════ */
interface DoItemProps {
  ancora: Ancora;
  /** Título sugerido para a primeira sala ("OS BZ-2026-014"). */
  tituloSugerido?: string;
  /**
   * Sem sala nenhuma, abre uma na hora com o `tituloSugerido` em vez de mostrar
   * o formulário. É para onde a conversa é OBVIAMENTE de uma coisa só — a etapa
   * do acompanhamento, que já tem nome. Dele, 04/09/2026: *"eu clico em
   * conversa dessa etapa e ele me oferece para abrir a sala"*.
   */
  abrirDireto?: boolean;
  /**
   * UM CHAT SÓ por item. Regra dele, 04/09/2026: *"só pode ter uma sala por
   * etapa — qual o sentido de ter várias salas dentro de uma etapa? nenhum"*.
   * Com isto ligado, assim que o chat existe a barra de seleção e o botão de
   * iniciar somem: não há o que escolher nem o que criar.
   */
  chatUnico?: boolean;
  /**
   * A SALA NASCE NA PRIMEIRA MENSAGEM. Sem sala, em vez do "Iniciar chat", a
   * caixa de escrever já aparece; a sala só é gravada quando alguém manda a
   * primeira mensagem — item onde ninguém conversou não guarda conversa vazia.
   * Dele, 29/10/2026, para a OS: "não faz sentido ter o registro de um campo
   * vazio". O servidor devolve a sala que já existir, então duas primeiras
   * mensagens ao mesmo tempo caem na mesma.
   */
  nasceNaPrimeira?: boolean;
  /** Repassados à sala: de qual etapa se está falando, e como rotulá-las. */
  etapaId?: number;
  numerosDasEtapas?: Record<number, string>;
}

export function CustomMensagensDoItem({
  ancora, tituloSugerido, abrirDireto, chatUnico, nasceNaPrimeira, etapaId, numerosDasEtapas,
}: DoItemProps) {
  const { pode } = useAuth();
  const [salas, setSalas] = useState<Sala[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [selecionada, setSelecionada] = useState<number | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  const [novaVisibilidade, setNovaVisibilidade] = useState<'herdada' | 'restrita'>('herdada');
  const [salvando, setSalvando] = useState(false);

  const podeAbrir = pode('mensagens.sala.gerenciar');
  const [primeira, setPrimeira] = useState('');
  const chave = `${ancora.tipo}:${ancora.id}`;

  const carregar = useCallback(async () => {
    try {
      const lista = await mensagensApi.salasDoItem(ancora);
      setSalas(lista);
      setSelecionada((atual) => (atual && lista.some((s) => s.id === atual) ? atual : (lista[0]?.id ?? null)));
      setErro(null);
    } catch (err) {
      setErro(erroParaTexto(err, 'Não deu para carregar o chat.'));
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  useEffect(() => {
    setCarregando(true);
    carregar();
  }, [carregar]);

  /* Enquanto a conversa não nasceu, a caixa de primeira mensagem fica parada —
     e quem estava nela não via a conversa que OUTRO abriu sem recarregar a
     página (rodada 3, 30/10/2026). Um polling leve, só neste estado: assim que
     a sala existe, a `CustomMensagensSala` assume e este intervalo morre. */
  useEffect(() => {
    if (carregando || salas.length > 0) return;
    const t = setInterval(carregar, 12000);
    return () => clearInterval(t);
  }, [carregando, salas.length, carregar]);

  const aoMudarSala = useCallback((s: Sala) => {
    setSalas((arr) => arr.map((x) => (x.id === s.id ? { ...x, ...s, participantes: s.participantes ?? x.participantes } : x)));
  }, []);

  async function abrir() {
    const t = (tituloSugerido || '').trim();
    if (!t) return;
    setSalvando(true);
    try {
      const s = await mensagensApi.abrirSala({
        titulo: t,
        /* ⛔ O CHAT DO ITEM NASCE ABERTO. Com `abrirDireto` ninguém escolhe
           visibilidade — e não deve mesmo: a regra da autorização é "quem vê o
           pedido vê a conversa". Deixar isto no estado do formulário criou duas
           salas RESTRITAS em 05/09, uma delas com uma menção à Adriana que ela
           nunca veria, porque não era participante. Corrigidas no banco em
           11/09; aqui se impede que volte. */
        visibilidade: abrirDireto ? 'herdada' : novaVisibilidade,
        ancoras: [{ tipo: ancora.tipo, id: ancora.id }],
      });
      setSalas((arr) => [s, ...arr]);
      setSelecionada(s.id);
      setAbrindo(false);

    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para abrir a sala.'));
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center gap-2 text-secondary small py-3">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }
  if (erro) return <div className="alert alert-light small mb-0">{erro}</div>;

  /* ⛔ O ASSUNTO SAIU DO FORMULÁRIO (05/09/2026). Dele: *"esse assunto do chat
     é a coisa mais estúpida que existe — o assunto do chat é a etapa"*. E ele
     tem razão: quem clica em "Iniciar chat" dentro de uma etapa já nomeou o
     assunto ao criar a etapa. O título da sala continua existindo (é o
     `tituloSugerido`), só deixou de ser perguntado. */
  const formAbrir = (
    <div className="casaa-mensagens-abrir d-flex flex-column gap-2 mb-3">
      <div className="form-floating">
        <select
          id={`casaa-mensagens-nova-visibilidade-${chave}`}
          className="form-select"
          value={novaVisibilidade}
          onChange={(e) => setNovaVisibilidade(e.target.value as 'herdada' | 'restrita')}
        >
          <option value="herdada">Quem vê este item vê o chat</option>
          <option value="restrita">Só quem eu adicionar</option>
        </select>
        <label htmlFor={`casaa-mensagens-nova-visibilidade-${chave}`}>Quem vê</label>
      </div>
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-theme" onClick={abrir} disabled={salvando}>
          {salvando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-plus-lg me-1"></i>}
          Iniciar chat
        </button>
        {salas.length > 0 && (
          <button type="button" className="btn btn-outline-secondary" onClick={() => setAbrindo(false)}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );

  async function mandarPrimeira() {
    const t = primeira.trim();
    const titulo = (tituloSugerido || '').trim();
    if (!t || !titulo || salvando) return;
    setSalvando(true);
    try {
      const s = await mensagensApi.abrirSala({
        titulo,
        visibilidade: 'herdada',
        ancoras: [{ tipo: ancora.tipo, id: ancora.id }],
      });
      await mensagensApi.enviar(s.id, { texto: t });
      setPrimeira('');
      await carregar();
    } catch (err) {
      toast.error(erroParaTexto(err, 'Não deu para enviar.'));
    } finally {
      setSalvando(false);
    }
  }

  if (salas.length === 0 && nasceNaPrimeira) {
    if (!pode('mensagens.sala.falar')) {
      return <p className="small text-secondary mb-0">Ninguém escreveu aqui ainda.</p>;
    }
    return (
      <div className="d-flex flex-column gap-2">
        <p className="small text-secondary mb-0">
          Ninguém escreveu aqui ainda. O que for combinado nesta conversa fica registrado na OS.
        </p>
        <div className="casaa-mensagens-compositor input-group align-items-end">
          <textarea
            className="form-control"
            rows={2}
            placeholder="Escreva a primeira mensagem…"
            aria-label="Mensagem"
            value={primeira}
            onChange={(e) => setPrimeira(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                mandarPrimeira();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-square btn-theme"
            title="Enviar"
            aria-label="Enviar"
            onClick={mandarPrimeira}
            disabled={salvando}
          >
            {salvando ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-send"></i>}
          </button>
        </div>
      </div>
    );
  }

  if (salas.length === 0) {
    if (abrirDireto && podeAbrir) {
      return (
        <div className="d-flex flex-column align-items-start gap-2">
          <p className="small text-secondary mb-0">
            Ainda não se falou nada por aqui. O que for combinado fica registrado nesta etapa.
          </p>
          <button type="button" className="btn btn-sm btn-theme" onClick={abrir} disabled={salvando}>
            {salvando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-chat-dots me-1"></i>}
            Iniciar chat
          </button>
        </div>
      );
    }
    return (
      <div className="d-flex flex-column gap-3">
        <p className="small text-secondary mb-0">Ainda não tem chat aqui. O que se combinar no chat fica registrado.</p>
        {podeAbrir ? formAbrir : null}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column">
      {/* Com `chatUnico`, nada de barra: não há sala para escolher nem outra
          para abrir. */}
      {!chatUnico && (salas.length > 1 || podeAbrir) && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {salas.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`btn btn-sm ${s.id === selecionada ? 'btn-theme' : 'btn-outline-secondary'} d-inline-flex align-items-center gap-1`}
              onClick={() => setSelecionada(s.id)}
            >
              {s.estado !== 'aberta' && <i className="bi bi-lock"></i>}
              <span className="text-truncate" style={{ maxWidth: '14rem' }}>{s.titulo}</span>
              {s.nao_lidas > 0 && s.id !== selecionada && <span className="badge text-bg-danger">{s.nao_lidas}</span>}
            </button>
          ))}
          {podeAbrir && !abrindo && (
            <button type="button" className="btn btn-sm btn-link text-decoration-none" onClick={() => setAbrindo(true)}>
              <i className="bi bi-plus-lg me-1"></i>Iniciar chat
            </button>
          )}
        </div>
      )}
      {abrindo && podeAbrir && formAbrir}
      {selecionada !== null && (
        <CustomMensagensSala
          key={selecionada}
          salaId={selecionada}
          etapaId={etapaId}
          numerosDasEtapas={numerosDasEtapas}
          aoMudar={aoMudarSala}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * O BADGE — quantas não lidas um item tem, para a aba
 * ═══════════════════════════════════════════════════════════════════════════ */
export function CustomMensagensBadge({ ancora }: { ancora: Ancora }) {
  const [total, setTotal] = useState(0);
  const [mencoes, setMencoes] = useState(0);
  const chave = `${ancora.tipo}:${ancora.id}`;
  useEffect(() => {
    let vivo = true;
    const buscar = () => {
      if (document.hidden) return;
      mensagensApi
        .salasDoItem(ancora)
        .then((lista) => {
          if (!vivo) return;
          setTotal(lista.reduce((n, s) => n + s.nao_lidas, 0));
          setMencoes(lista.reduce((n, s) => n + (s.mencoes_nao_lidas ?? 0), 0));
        })
        .catch(() => undefined);
    };
    buscar();
    const t = setInterval(buscar, INTERVALO_BADGE_MS);
    return () => {
      vivo = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);
  if (total === 0) return null;
  /* ⛔ DOIS SINAIS, DUAS MARCAS. Dele, 11/09: *"se você receber uma menção,
     aquela aba tem que estar marcada — e tem a questão de se teve mensagem nova
     ou não"*. O número diz QUANTAS não lidas; o ponto diz que uma delas CHAMA
     VOCÊ. Somar os dois num número só esconderia justamente o que não espera. */
  return (
    <span className="casaa-mensagens-marca ms-1">
      <span className={`badge ${mencoes > 0 ? 'text-bg-danger' : 'text-bg-secondary'}`}>{total}</span>
      {mencoes > 0 && (
        <i
          className="bi bi-at casaa-mensagens-mencionado"
          title={mencoes === 1 ? 'Chamaram você' : `Chamaram você em ${mencoes} mensagens`}
        ></i>
      )}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * A PRÉVIA — o chat visto de fora, sem sair de onde se está
 * ═══════════════════════════════════════════════════════════════════════════
 * Dele, 11/09/2026: *"uma mini representaçãozinha do chat, para se as pessoas
 * tiverem conversando alguma coisa elas já verem na hora que entrarem na
 * autorização... e pode aparecer dentro do acompanhamento e dentro do card"*.
 *
 * ⚠️ **Não busca nada de novo.** A `Sala` que `salasDoItem` já devolve traz
 * `ultima_mensagem`, `nao_lidas`, `mencoes_nao_lidas` e `participantes` — tudo
 * o que a prévia mostra. Era a condição que ele pôs: *"a gente tem que pensar
 * numa solução que não vai ficar criando código atrás de código"*.
 *
 * ⛔ **NÃO É SÓ PARA OLHAR — É PARA INTERAGIR.** A primeira versão mostrava a
 * última fala e um link, e ele corrigiu na hora (11/09): *"minha ideia da
 * conversa é eu ter esse painel, ver as últimas mensagens pelo menos e
 * aproveitar e já mandar a mensagem aí; não é só para olhar, é para interagir"*.
 *
 * Por isso ela NÃO desenha conversa nenhuma: ela monta a **sala de verdade**
 * (`CustomMensagensSala`, sem cabeçalho) numa caixa mais baixa. Mural que rola,
 * campo de escrever, menção, anexo — tudo o que a aba tem, no painel. O que muda
 * é só a ALTURA, e altura é CSS. Era a condição dele: *"uma solução que não vai
 * ficar criando código atrás de código"*.
 */
export function CustomMensagensPrevia({
  ancora, aoAbrir, titulo = 'Conversa', etapaId, numerosDasEtapas,
}: {
  ancora: Ancora;
  /** "Ver a conversa toda" — trocar de aba, navegar, o que for. */
  aoAbrir?: () => void;
  titulo?: string;
  etapaId?: number;
  numerosDasEtapas?: Record<number, string>;
}) {
  const [sala, setSala] = useState<Sala | null>(null);
  const [carregando, setCarregando] = useState(true);
  const chave = `${ancora.tipo}:${ancora.id}`;

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    mensagensApi
      .salasDoItem(ancora)
      .then((lista) => vivo && setSala(lista[0] ?? null))
      .catch(() => undefined)
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  if (carregando || !sala) return null;

  const mencionado = (sala.mencoes_nao_lidas ?? 0) > 0;

  return (
    <div className={`card adminuiux-card casaa-conversa-widget${mencionado ? ' casaa-conversa-widget-mencionado' : ''}`}>
      <div className="card-header bg-transparent d-flex align-items-center gap-2">
        <h6 className="mb-0">{titulo}</h6>
        {sala.nao_lidas > 0 && (
          <span className={`badge ${mencionado ? 'text-bg-danger' : 'text-bg-secondary'}`}>
            {sala.nao_lidas}
          </span>
        )}
        {mencionado && <i className="bi bi-at casaa-mensagens-mencionado" title="Chamaram você"></i>}
        {aoAbrir && (
          <button type="button" className="btn btn-sm btn-link p-0 ms-auto" onClick={aoAbrir}>
            Ver a conversa toda
          </button>
        )}
      </div>
      <div className="card-body">
        <CustomMensagensSala
          salaId={sala.id}
          etapaId={etapaId}
          numerosDasEtapas={numerosDasEtapas}
          semCabecalho
          soFalas
        />
      </div>
    </div>
  );
}
